import {
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogClose,
  Typography,
  Button,
} from '@ui/components';
import { cdnVehiclesTemplatePath, getCdnGuidelinesPath } from '@shared/utils';
import { useVehicleMassImportStore } from '@pages/vehicles/store';
import { useTranslation } from 'react-i18next';
import { FC } from 'react';

import { FileUploader } from './file-uploader/file-uploader';

export const VehicleMassImportFileUpload: FC<{
  uploadFile: (file: File) => void;
}> = ({ uploadFile }) => {
  const { t } = useTranslation();
  const { file, isUploadFilePending, fileUploadError, resetFile, showSummary } =
    useVehicleMassImportStore();

  return (
    <DialogContent className="max-w-xl">
      <DialogHeader>
        <DialogTitle>{t('VEHICLES.MASS_IMPORT.TITLE')}</DialogTitle>
      </DialogHeader>

      <section className="p-4 space-y-8">
        <div className="space-y-4">
          <Typography variant="paragraphBasicBook">
            {t('VEHICLES.MASS_IMPORT.DESCRIPTION_1')}
          </Typography>

          <Typography variant="paragraphBasicBook">
            {t('VEHICLES.MASS_IMPORT.DESCRIPTION_2')}
          </Typography>

          <Typography variant="paragraphBasicBook">
            {t('VEHICLES.MASS_IMPORT.DESCRIPTION_3')}
          </Typography>
        </div>

        <div className="flex items-center gap-3">
          <Button variant="secondary" asChild>
            <a href={cdnVehiclesTemplatePath} rel="noreferrer" target="_blank">
              {t('VEHICLES.MASS_IMPORT.DOWNLOAD_CSV_TEMPLATE')}
            </a>
          </Button>

          <Button variant="link" asChild>
            <a href={getCdnGuidelinesPath()} rel="noreferrer" target="_blank">
              {t('VEHICLES.MASS_IMPORT.SHOW_PDF_INSTRUCTIONS')}
            </a>
          </Button>
        </div>

        <div className="space-y-4">
          <Typography variant="paragraphBasicBook" className="mt-4">
            {t('VEHICLES.MASS_IMPORT.FIELDS_DESCRIPTION')}
          </Typography>
          <div>
            <Typography variant="paragraphBasicBook" className="mb-2">
              {t('VEHICLES.MASS_IMPORT.FIELDS.TITLE')}
            </Typography>
            <ul className="list-disc pl-6">
              <li>
                <Typography variant="paragraphBasicBook">
                  {t('VEHICLES.MASS_IMPORT.FIELDS.REGISTRATION_NUMBER')}
                </Typography>
              </li>
              <li>
                <Typography variant="paragraphBasicBook">
                  {t('VEHICLES.MASS_IMPORT.FIELDS.REGISTRATION_COUNTRY')}
                </Typography>
              </li>
              <li>
                <Typography variant="paragraphBasicBook">
                  {t('VEHICLES.MASS_IMPORT.FIELDS.CATEGORY')}
                </Typography>
              </li>
              <li>
                <Typography variant="paragraphBasicBook">
                  {t('VEHICLES.MASS_IMPORT.FIELDS.COST_CENTER')}
                </Typography>
              </li>
              <li>
                <Typography variant="paragraphBasicBook">
                  {t('VEHICLES.MASS_IMPORT.FIELDS.NAME')}
                </Typography>
              </li>
              <li>
                <Typography variant="paragraphBasicBook">
                  {t('VEHICLES.MASS_IMPORT.FIELDS.LABELS')}
                </Typography>
              </li>
            </ul>
          </div>
        </div>

        <FileUploader
          fileChange={(file) => {
            if (!file) return resetFile();
            uploadFile(file);
          }}
          errorMessage={fileUploadError ?? undefined}
          isPending={isUploadFilePending}
          file={file}
        />
      </section>
      {file && !isUploadFilePending && (
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="tertiary" size="large" wide>
              {t('VEHICLES.MASS_IMPORT.CANCEL')}
            </Button>
          </DialogClose>

          <Button onClick={showSummary} variant="primary" size="large" wide>
            {t('VEHICLES.MASS_IMPORT.UPLOAD')}
          </Button>
        </DialogFooter>
      )}
    </DialogContent>
  );
};
